import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables, TablesUpdate } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { queryKeys } from "./keys";

export type Profile = Tables<"profiles">;
export type ProfileUpdate = TablesUpdate<"profiles">;

/**
 * Hook : profil de l'utilisateur courant.
 *
 * - Skip si l'utilisateur n'est pas connecté.
 * - Cache par userId pour permettre l'invalidation ciblée.
 */
export function useMyProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? queryKeys.profile.byUserId(user.id) : ["profile", "anonymous"],
    queryFn: async (): Promise<Profile | null> => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(user),
  });
}

/**
 * Hook : profil arbitraire (admin / chef projet).
 */
export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.profile.byUserId(userId) : ["profile", "none"],
    queryFn: async (): Promise<Profile | null> => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(userId),
  });
}

/**
 * Mutation : mise à jour du profil de l'utilisateur courant.
 */
export function useUpdateMyProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: ProfileUpdate): Promise<Profile> => {
      if (!user) throw new Error("Non connecté");
      const { data, error } = await supabase
        .from("profiles")
        .update(patch)
        .eq("user_id", user.id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (user) {
        queryClient.setQueryData(queryKeys.profile.byUserId(user.id), data);
      }
    },
  });
}
